import { api } from "@/lib/api";
import {
  AccommodationWithFulfillments,
  AccommodationWithFulfillmentsDtoSchema,
  accommodationWithFulfillmentsFromDto,
  accommodationWithFulfillmentsToDto,
  CreateFulfillment,
  createFulfillmentToDto,
  FulfillmentDtoSchema,
  fulfillmentFromDto,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import { getAccommodationWithFulfillmentsQueryOptions } from "./get.hooks";

export function useCreateAccommodationFulfillment(opts?: {
  onSuccess?: (guide: AccommodationWithFulfillments) => void;
  onError?: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({
      accommodation,
      fulfillment: createFulfillment,
      accomodationPrice,
    }: {
      fulfillment: CreateFulfillment;
      accommodation: AccommodationWithFulfillments;
      accomodationPrice?: number;
    }) => {
      const fulfillmentResult = await api.post(
        "/api/fulfillments",
        createFulfillmentToDto(createFulfillment),
        FulfillmentDtoSchema,
      );

      const fulfillment = fulfillmentFromDto(fulfillmentResult);

      const fulfillments = [...(accommodation.fulfillments ?? []), fulfillment];

      const updateAccommodationDto = await api.put(
        "/api/accommodations",
        accommodationWithFulfillmentsToDto({
          ...accommodation,
          fulfillments,
        }),
        AccommodationWithFulfillmentsDtoSchema,
      );

      const newAcc = accommodationWithFulfillmentsFromDto(
        updateAccommodationDto,
      );
      newAcc.fulfillments = fulfillments;
      newAcc.balance =
        (accomodationPrice ?? 0) -
        fulfillments.reduce((acc, item) => acc + (item.value ?? 0), 0);
      newAcc.isPayed = false;
      return newAcc;
    },
    onSuccess: (newAcc) => {
      const queryKey = getAccommodationWithFulfillmentsQueryOptions(
        newAcc.reservationId?.toString() ?? "",
      ).queryKey;
      queryClient.setQueryData<AccommodationWithFulfillments[]>(
        queryKey,
        (oldData) => oldData?.map((d) => (d.id === newAcc.id ? newAcc : d)),
      );
      toast({
        title: "toast.fulfillmentCreated",
        variant: "success",
      });
      opts?.onSuccess?.(newAcc);
    },
    onError: () => {
      toast({
        title: "toast.createFulfillmentError",
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
